
import { createContext, useContext, useState, useEffect } from "react";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [price, setPrice] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [count, setCount] = useState(0);

  // Load cart from localStorage
  useEffect(() => {
    try {
      const storedCart = localStorage.getItem("cart");

      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        setCart(parsedCart);

        // Recalculate price and count from stored cart
        setPrice(
          parsedCart.reduce(
            (total, item) => total + item.price * item.quantity,
            0
          )
        );

        setCount(
          parsedCart.reduce(
            (total, item) => total + item.quantity,
            0
          )
        );
      }
    } catch (error) {
      console.error("Error loading cart from localStorage:", error);
    }

    setIsLoaded(true);
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem("cart", JSON.stringify(cart));
      localStorage.setItem("price", String(price));
    }
  }, [cart, price, isLoaded]);

  // Add item to cart
  const addToCart = (item, quantity) => {
    setCart((prevCart) => {
      // Match by product ID AND color
      const existingItem = prevCart.find(
        (cartItem) =>
          cartItem.id === item.id &&
          cartItem.color === item.color
      );

      let newCart;

      if (existingItem) {
        // Same product and color: increase quantity
        newCart = prevCart.map((cartItem) =>
          cartItem.id === item.id &&
          cartItem.color === item.color
            ? {
                ...cartItem,
                quantity: cartItem.quantity + quantity,
              }
            : cartItem
        );
      } else {
        // Different color or new product: add separate entry
        newCart = [...prevCart, { ...item, quantity }];
      }

      setCount(
        newCart.reduce(
          (total, cartItem) => total + cartItem.quantity,
          0
        )
      );

      setPrice(
        newCart.reduce(
          (total, cartItem) =>
            total + cartItem.price * cartItem.quantity,
          0
        )
      );

      return newCart;
    });
  };

  // Remove a specific product/color combination
  const removeFromCart = (item) => {
    setCart((prevCart) => {
      const newCart = prevCart.filter(
        (cartItem) =>
          !(
            cartItem.id === item.id &&
            cartItem.color === item.color
          )
      );

      setPrice(
        newCart.reduce(
          (total, cartItem) =>
            total + cartItem.price * cartItem.quantity,
          0
        )
      );

      setCount(
        newCart.reduce(
          (total, cartItem) => total + cartItem.quantity,
          0
        )
      );

      return newCart;
    });
  };

  // Change quantity for a specific product/color combination
  const changeCart = (item, quantity) => {
    setCart((prevCart) => {
      // Treat zero or negative quantity as removal
      const newCart =
        quantity <= 0
          ? prevCart.filter(
              (cartItem) =>
                !(
                  cartItem.id === item.id &&
                  cartItem.color === item.color
                )
            )
          : prevCart.map((cartItem) =>
              cartItem.id === item.id &&
              cartItem.color === item.color
                ? { ...cartItem, quantity }
                : cartItem
            );

      setPrice(
        newCart.reduce(
          (total, cartItem) =>
            total + cartItem.price * cartItem.quantity,
          0
        )
      );

      setCount(
        newCart.reduce(
          (total, cartItem) => total + cartItem.quantity,
          0
        )
      );

      return newCart;
    });
  };

  // Clear entire cart
  const deleteAllCart = () => {
    setCart([]);
    setPrice(0);
    setCount(0);

    localStorage.removeItem("cart");
    localStorage.removeItem("price");
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        price,
        count,
        addToCart,
        removeFromCart,
        changeCart,
        deleteAllCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
